import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  FormAutocomplete,
  formClasses,
  labelClasses,
  LabeledAutocomplete,
} from "../../../../../../../styles/AutocompleteStyles";
import { getModifiedScrollHeight } from "../../../../../../../utils/UtilFunctions";
import { TextField } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import {
  GetSuppliers,
  GetSuppliersForPO,
  GetSuppliersForPOOptions,
} from "../../../../masters/supplier/queries/queries";
import {
  GetSuppliersForPOOptionsEdges,
  GetSuppliersForPOOptionsResult,
  GetSuppliersForPOOptionsVars,
} from "../../../queries/types";
import { AppContext } from "../../../../../../../context/context";
import useUrlParams from "../../../../../../../customHooks/useUrlParams";

import {
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
} from "../../../../../../../utils/constants";
import { OrderDirection } from "../../../../../../../utils/commonQueryTypes";
import {
  AtsAcLdgrOrderField,
  GetSuppliersResult,
  GetSuppliersVars,
  LdgrQueryType,
} from "../../../../masters/supplier/queries/types";
import { responseType } from "../../../../../../../utils/formTypes";
import { SuppplierSelectProps } from "./SuppliersSelect";

const SuppliersAutocomplete = ({
  onChange,
  value,
  onKeyDown,
  autoFocus,
  label,
  forPo,
  placeholder,
  input,
}: SuppplierSelectProps) => {
  
  const { customer_id } = useUrlParams();
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<GetSuppliersForPOOptionsEdges[]>(
    []
  );
  const [autoValue, setAutoValue] = useState<responseType | null>(null);

  const [hasNextPage, setHasNextPage] = useState(true);
  const [searchData, setSearchData] = useState("");

  const { state } = useContext(AppContext);
  const Query = forPo ? GetSuppliersForPO : GetSuppliers;
  const [GetSupplier, { data, loading, fetchMore }] = useLazyQuery<
    GetSuppliersForPOOptionsResult | GetSuppliersResult,
    GetSuppliersForPOOptionsVars | GetSuppliersVars
  >(Query);

  useEffect(() => {
    if (data && !loading) {
      const newData = forPo
        ? (data as GetSuppliersForPOOptionsResult).GetSuppliersForPO.edges
        : (data as GetSuppliersResult).GetSuppliers.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = suppliers.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setSuppliers(updatedNewData);
      } else {
        setSuppliers(newData);
      }
      const newCursor = forPo
        ? (data as GetSuppliersForPOOptionsResult).GetSuppliersForPO.pageInfo
            .endCursor
        : (data as GetSuppliersResult).GetSuppliers.pageInfo.endCursor;
      setEndCursor(newCursor);
    }
    // eslint-disable-next-line
  }, [data, loading, forPo]);

  useEffect(() => {
    if (customer_id) {
      const modVars = forPo
        ? ({
            after: null,
            customer_id,
            finYrId: state.ActiveFinYr ? state.ActiveFinYr.id : 0,
            first: ROWS_PER_PAGE,
            orderBy: {
              direction: OrderDirection.ASC,
              field: AtsAcLdgrOrderField.CREATED_AT,
            },
            
            name: searchData,
          } as GetSuppliersForPOOptionsVars)
        : ({
            
            after: null,
            first: ROWS_PER_PAGE,
            customer_id,
            input,
            orderBy: {
              direction: OrderDirection.ASC,
              field: AtsAcLdgrOrderField.CREATED_AT,
            },
            name: searchData,
          } as GetSuppliersVars);
      GetSupplier({
        variables: modVars,
      });
    }
  }, [customer_id, GetSupplier, state.ActiveFinYr, forPo, input, searchData]);

  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    const dataMod = forPo
      ? (data as GetSuppliersForPOOptionsResult).GetSuppliersForPO
      : (data as GetSuppliersResult).GetSuppliers;

    if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
      if (hasNextPage && !loading) {
        fetchMore({
          variables: {
            first: FETCH_MORE_DATA,
            after: endCursor,
          },
          updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prevResult;
            const fetchMoreResultMod = forPo
              ? (fetchMoreResult as GetSuppliersForPOOptionsResult)
                  .GetSuppliersForPO
              : (fetchMoreResult as GetSuppliersResult).GetSuppliers;

            const newEdges = fetchMoreResultMod.edges;
            const pageInfo = fetchMoreResultMod.pageInfo;
            setEndCursor(pageInfo.endCursor);
            setHasNextPage(pageInfo.hasNextPage);

            const duplicateCheck = fetchMoreResultMod.edges.filter(
              ({ node: { id } }) =>
                newEdges.findIndex(
                  ({ node: { id: newId } }) => newId === id
                ) !== -1
            );

            if (duplicateCheck.length > 0) return prevResult;

            return {
              GetSuppliersForPO: {
                edges: [...suppliers, ...newEdges],
                pageInfo,
                totalCount: data ? dataMod.totalCount! : 0,
              },
            };
          },
        });
      }
    }
  };

  const supplierOptions = useMemo(() => {
    return suppliers.map((sup) => {
      return {
        label: sup.node.desc,
        value: sup.node.id,
      };
    });
  }, [suppliers]);

  useEffect(() => {
    if (value) {
      const foundValue = supplierOptions.find(
        ({ value: optionsValue }) => Number(optionsValue) === Number(value)
      );

      setAutoValue(foundValue ? foundValue : null);
    } else {
      setAutoValue(null);
    }
  }, [value, data, supplierOptions]);

  let commonAutocompleteClassName = label
    ? labelClasses.inputRoot
    : formClasses.inputRoot;
  let commonTextFieldClassName = label
    ? labelClasses.formControlRoot
    : formClasses.formControlRoot;

  const Autocomplete = label ? LabeledAutocomplete : FormAutocomplete;

  return (
    <Autocomplete
      className={commonAutocompleteClassName}
      options={supplierOptions}
      onKeyDown={onKeyDown}
      slotProps={{
        listbox: {
          onScroll: handleScroll,
        },
      }}
      value={autoValue}
      onChange={(e, newValue) => {
        if (onChange) onChange(newValue as responseType);
      }}

    clearOnEscape
    clearIcon
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          autoFocus={autoFocus}
          label={label ? label : ""}
          onChange={(e) => {
            setSearchData(e.target.value);
          }}
          className={commonTextFieldClassName}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          placeholder={placeholder}
        />
      )}
    />
  );
};

export default SuppliersAutocomplete;
